import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MailCheck, MailX, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useResendVerification } from "@/components/VerifyEmailSheet";

export default function VerifyEmail() {
  const { token } = useParams();
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState("loading");
  const { resend, cooldown, busy } = useResendVerification();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/verify-email", { token })
      .then(async ({ data }) => { setState(data.already_verified ? "already" : "success"); try { await refresh(); } catch {} })
      .catch(() => setState("error"));
  }, [token, refresh]);
  const ok = state === "success" || state === "already";
  return (
    <div className="page fade-up" data-testid="verify-page">
      <div className="min-h-[60vh] flex flex-col items-start justify-center py-12 max-w-md">
        {state === "loading" && <div data-testid="verify-loading" className="flex items-center gap-2 text-zinc-500"><Loader2 className="animate-spin" size={18} /> {t("verify_loading")}</div>}
        {ok && (
          <div data-testid="verify-success">
            <div className="w-14 h-14 rounded-2xl bg-[#e8f7ec] flex items-center justify-center"><MailCheck size={28} className="text-green-700" /></div>
            <h1 className="font-display text-2xl font-extrabold mt-4">{state === "already" ? t("verify_already") : t("verify_done")}</h1>
            <p className="text-sm text-zinc-500 mt-2">{t("verify_success_body")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={user ? "/perfil" : "/login"} data-testid="verify-continue-btn" className="inline-flex items-center px-5 py-3 rounded-full bg-black text-white text-sm font-semibold hover:bg-zinc-800">{user ? t("verify_continue") : t("login")}</Link>
              <Link to="/" data-testid="verify-home-btn" className="inline-flex items-center px-5 py-3 rounded-full border border-zinc-300 bg-white text-sm font-semibold hover:border-black">{t("back_home")}</Link>
            </div>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-error">
            <div className="w-14 h-14 rounded-2xl bg-[#fdecec] flex items-center justify-center"><MailX size={28} className="text-[#c8102e]" /></div>
            <h1 className="font-display text-2xl font-extrabold mt-4">{t("verify_invalid_title")}</h1>
            <p className="text-sm text-zinc-500 mt-2">{t("verify_invalid_body")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {user && user.email_verified === false && (
                <button onClick={resend} disabled={busy || cooldown > 0} data-testid="verify-page-resend-btn"
                        className="inline-flex items-center px-5 py-3 rounded-full bg-[#c8102e] text-white text-sm font-semibold disabled:opacity-50">
                  {cooldown > 0 ? `${t("verify_resend")} (${cooldown}s)` : t("verify_resend")}
                </button>
              )}
              {!user && <Link to="/login" data-testid="verify-login-btn" className="inline-flex items-center px-5 py-3 rounded-full bg-black text-white text-sm font-semibold">{t("login")}</Link>}
              <Link to="/" data-testid="verify-home-btn" className="inline-flex items-center px-5 py-3 rounded-full border border-zinc-300 bg-white text-sm font-semibold hover:border-black">{t("back_home")}</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
